/**
 * Coloured square tiles that identify each kind of record (leads, accounts…) in headers and cards.
 * Simple original glyphs on a white stroke; the colour carries identity, the page title names it.
 * Each glyph is drawn in parts: the meaningful part moves once when the card or link it sits in is hovered or
 * focused (a deal's trend line draws, a task's tick draws, a company's windows light up). Styles: .obj-icon .m-*.
 */
type Part = [d: string, motion?: string];
const GLYPHS: Record<string, { color: string; parts: Part[] }> = {
  // A person with a spark: a new prospect.
  lead: { color: 'var(--obj-lead)', parts: [['M12 12a3.2 3.2 0 1 0 0-6.4A3.2 3.2 0 0 0 12 12Zm-5.5 6.5c.6-2.9 2.9-4.6 5.5-4.6s4.9 1.7 5.5 4.6'], ['M18.5 4.5v3M17 6h3', 'm-pop']] },
  // A building; its windows light up.
  account: { color: 'var(--obj-account)', parts: [['M6 19V6.5L12 4l6 2.5V19M4.5 19h15M11 19v-3h2v3'], ['M9 9h1.5M13.5 9H15M9 12.5h1.5M13.5 12.5H15', 'm-blink']] },
  // A contact card; its lines slide.
  contact: { color: 'var(--obj-contact)', parts: [['M4.5 6.5h15v11h-15z'], ['M9 12.2a1.7 1.7 0 1 0 0-3.4 1.7 1.7 0 0 0 0 3.4ZM6.4 15.5c.4-1.4 1.4-2.1 2.6-2.1s2.2.7 2.6 2.1'], ['M13.5 10h3.5M13.5 13h3.5', 'm-slide']] },
  // A rising trend: a deal moving forward; the line draws itself.
  opportunity: { color: 'var(--obj-opportunity)', parts: [['M4.5 18.5h15'], ['M6 15l4-4 3 2.5 5-5.5M14.5 8H18v3.5', 'm-draw']] },
  // A megaphone; its sound wave moves out.
  campaign: { color: 'var(--obj-campaign)', parts: [['M5 10.5v3h2.5L13 17V7l-5.5 3.5H5Z'], ['M16 9.5c.8.7 1.2 1.6 1.2 2.5s-.4 1.8-1.2 2.5', 'm-slide']] },
  product: { color: 'var(--obj-product)', parts: [['M12 4.5 19 8v8l-7 3.5L5 16V8l7-3.5ZM5 8l7 3.5L19 8M12 11.5v8', 'm-pop']] },
  // A report; its bars grow.
  report: { color: 'var(--obj-report)', parts: [['M7 4.5h7l3.5 3.5v11.5H7zM14 4.5V8h3.5'], ['M9.5 16v-2.5M12 16v-4.5M14.5 16v-3', 'm-draw']] },
  // A gauge; its needle swings.
  dashboard: { color: 'var(--obj-dashboard)', parts: [['M5 15a7 7 0 0 1 14 0M4.5 18.5h15'], ['M12 15l3.2-3.6', 'm-needle']] },
  forecast: { color: 'var(--obj-forecast)', parts: [['M4.5 18.5h15M6.5 16V12M10.5 16V9.5M14.5 16v-5'], ['M18.5 6.5l-4 2.5-4-2-4 2.5', 'm-draw']] },
  // A calendar page; its tick draws itself.
  activity: { color: 'var(--obj-activity)', parts: [['M6 5.5h12v14H6zM9 4v3M15 4v3'], ['M8.5 13l2.2 2.2 4.6-4.6', 'm-draw']] },
  custom: { color: 'var(--obj-custom)', parts: [['M12 4.5 19 8.2v7.6L12 19.5l-7-3.7V8.2L12 4.5ZM12 12l7-3.8M12 12v7.5M12 12 5 8.2', 'm-pop']] },
};

export function ObjectIcon({ name, size = 'm' }: { name: string; size?: 's' | 'm' | 'l' }) {
  const g = GLYPHS[name] ?? GLYPHS.custom!;
  return (
    <span className={`obj-icon ${size}`} style={{ background: g.color }} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        {g.parts.map(([d, motion], i) => <path key={i} d={d} className={motion} pathLength={motion === 'm-draw' ? 1 : undefined} />)}
      </svg>
    </span>
  );
}
